import { useState } from 'react';
import { activeTrips, pastTrips } from '../game/trips';
import { tripScore } from '../game/scoring';
import { formatCodeForDisplay, normaliseJoinCode } from '../game/codes';
import { useGame } from '../state/GameProvider';
import { FriendsCard } from '../components/FriendsCard';
import { Leaderboard } from '../components/Leaderboard';
import { ShareTripButton } from '../components/ShareCard';
import type { TripState } from '../game/types';

/**
 * Trips are an optional overlay on lifetime mode, not the main loop.
 *
 * A trip can be started with no signal and only becomes joinable once the server
 * has seen it, so the code is shown as pending until then rather than offering a
 * code that nobody could actually use.
 */
export function Trips() {
  const { state, publicId, beginTrip, finishTrip, switchTrip, joinTrip } = useGame();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const running = activeTrips(state);
  const finished = pastTrips(state);

  const create = () => {
    beginTrip(name.trim() || 'Road trip');
    setName('');
  };

  const handleJoin = async () => {
    setJoinError(null);

    if (!normaliseJoinCode(code)) {
      setJoinError('That is not a valid trip code. They look like GALAH-42.');
      return;
    }

    setJoining(true);
    try {
      await joinTrip(code);
      setCode('');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setJoinError(
        message.includes('404')
          ? 'No trip is running with that code. Check it and try again.'
          : message.includes('429')
            ? 'Too many attempts. Wait a minute and try again.'
            : 'Could not join that trip. Are you online?',
      );
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="screen">
      <h1 className="screen-title">Road trips</h1>
      <p className="screen-sub">
        On a trip every plate is claimable again and the score starts from zero.
        Anything new to you still lands in your lifetime collection.
      </p>

      <div className="card">
        <h2>Start a trip</h2>
        <input
          className="search"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Sydney to Melbourne"
          aria-label="Trip name"
          maxLength={60}
        />
        <div style={{ height: 10 }} />
        <button type="button" className="btn primary" onClick={create}>
          Start trip
        </button>
      </div>

      <div className="card">
        <h2>Join a mate&rsquo;s trip</h2>
        <p>Get their trip code and enter it here. You&rsquo;ll need signal for this bit.</p>
        <input
          className="search"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="galah-42"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Trip code"
        />
        {joinError && <p className="field-error">{joinError}</p>}
        <div style={{ height: 10 }} />
        <button
          type="button"
          className="btn"
          onClick={() => void handleJoin()}
          disabled={joining || code.trim().length === 0}
        >
          {joining ? 'Joining…' : 'Join trip'}
        </button>
      </div>

      <FriendsCard />

      {state.activeTripId && (
        <button type="button" className="btn" onClick={() => switchTrip(null)}>
          Back to lifetime mode
        </button>
      )}

      {running.length > 0 && (
        <>
          <h2 className="screen-title">Running</h2>
          {running.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              isActive={trip.id === state.activeTripId}
              meId={publicId}
              onResume={() => switchTrip(trip.id)}
              onFinish={() => finishTrip(trip.id)}
            />
          ))}
        </>
      )}

      {finished.length > 0 && (
        <>
          <h2 className="screen-title">Finished</h2>
          {finished.map((trip) => (
            <div className="card" key={trip.id}>
              <h2>{trip.name}</h2>
              <TripSummary
                trip={trip}
                suffix={new Date(trip.startedAt).toLocaleDateString('en-AU')}
              />
              {trip.leaderboard.length > 1 && (
                <Leaderboard entries={trip.leaderboard} meId={publicId} />
              )}
              <div style={{ height: 10 }} />
              <ShareTripButton trip={trip} meId={publicId} />
            </div>
          ))}
        </>
      )}

      {running.length === 0 && finished.length === 0 && (
        <div className="empty">
          No trips yet. Start one when you set off, and send the code to whoever
          else is in the car.
        </div>
      )}
    </div>
  );
}

/**
 * The score-and-plates line. Both lists show it and only the tail differs - the
 * running list ends with whether this is the trip being played, the finished one
 * with the date it happened.
 */
function TripSummary({ trip, suffix }: { trip: TripState; suffix?: string }) {
  return (
    <p>
      {tripScore(trip).toLocaleString()} points ·{' '}
      {Object.keys(trip.myClaims).length} plates
      {suffix ? ` · ${suffix}` : ''}
    </p>
  );
}

function TripCard({ trip, isActive, meId, onResume, onFinish }: {
  trip: TripState;
  isActive: boolean;
  /** The viewer's public id, for the board - never the player id. */
  meId: string | null;
  onResume: () => void;
  onFinish: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(formatCodeForDisplay(trip.joinCode));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the code is on screen to read out anyway.
    }
  };

  return (
    <div className="card">
      <h2>{trip.name}</h2>
      <TripSummary trip={trip} suffix={isActive ? 'playing now' : undefined} />

      {trip.registered ? (
        <>
          <div className="code-display" style={{ fontSize: '1rem' }}>
            {formatCodeForDisplay(trip.joinCode)}
          </div>
          <div style={{ height: 8 }} />
          <button type="button" className="btn" onClick={() => void copyCode()}>
            {copied ? 'Copied' : 'Copy trip code'}
          </button>
        </>
      ) : (
        <div className="status-line">
          <span className="status-dot pending" />
          <span>Trip code appears once you have signal</span>
        </div>
      )}

      {trip.leaderboard.length > 1 && (
        <Leaderboard entries={trip.leaderboard} meId={meId} />
      )}

      <div style={{ height: 10 }} />
      {!isActive && (
        <button type="button" className="btn" onClick={onResume}>
          Resume this trip
        </button>
      )}
      <button type="button" className="btn danger" onClick={onFinish}>
        Finish trip
      </button>
    </div>
  );
}
